import { useEffect, useState } from 'react';
import { Header } from '../components/Header';
import { apiService } from '../services/api';

interface DashboardSummary {
  date: string;
  sendActions: {
    total: number;
    metrics?: {
      totalRecipients?: number;
      sent?: number;
      delivered?: number;
      read?: number;
      failed?: number;
    };
  };
  conversations: {
    total: number;
    unread: number;
    active: number;
  };
  messages: {
    total: number;
    today: number;
    inbound: number;
    outbound: number;
  };
  sessions: {
    total: number;
    active: number;
  };
}

const formatNumber = (value?: number) => new Intl.NumberFormat('pt-BR').format(value || 0);

const socialSentiment = {
  positive: 46,
  neutral: 34,
  negative: 20,
  votingIntent: 58,
  mentions: 18420,
  trend: '+7,4%',
};

const monitoredTags = [
  { tag: '#VoxCampaign', mentions: 5240, sentiment: 68, channel: 'Instagram', trend: '+18%' },
  { tag: '#NomeDoCandidato', mentions: 4890, sentiment: 61, channel: 'X/Twitter', trend: '+11%' },
  { tag: '#MudancaNaCidade', mentions: 3210, sentiment: 54, channel: 'TikTok', trend: '+6%' },
  { tag: '#DebateMunicipal', mentions: 2810, sentiment: 42, channel: 'Facebook', trend: '-3%' },
  { tag: '#Eleicao2026', mentions: 2270, sentiment: 49, channel: 'X/Twitter', trend: '+2%' },
];

const narrativeAlerts = [
  {
    title: 'Crescimento de apoio espontâneo',
    description: 'Menções positivas associadas a propostas locais subiram nas últimas 24h.',
    tone: 'positive',
  },
  {
    title: 'Atenção a críticas sobre infraestrutura',
    description: 'Termos negativos aparecem concentrados em bairros periféricos e grupos comunitários.',
    tone: 'negative',
  },
  {
    title: 'Debate gerando indecisos',
    description: 'Conversas neutras indicam eleitores pedindo comparação objetiva de propostas.',
    tone: 'neutral',
  },
];

export function DashboardPage() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadSummary = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getDashboardSummary();
      setSummary(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar visão geral';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const cards = [
    {
      label: 'Campanhas',
      value: summary?.sendActions.total,
      hint: `${formatNumber(summary?.sendActions.metrics?.sent)} envios disparados`,
    },
    {
      label: 'Eleitores impactados',
      value: summary?.sendActions.metrics?.totalRecipients,
      hint: `${formatNumber(summary?.sendActions.metrics?.delivered)} entregues`,
    },
    {
      label: 'Mensagens hoje',
      value: summary?.messages.today,
      hint: `${formatNumber(summary?.messages.outbound)} enviadas | ${formatNumber(summary?.messages.inbound)} recebidas`,
    },
    {
      label: 'Sessões ativas',
      value: summary?.sessions.active,
      hint: `${formatNumber(summary?.sessions.total)} conexões cadastradas`,
    },
  ];

  return (
    <>
      <Header
        title="Visão geral"
        subtitle="Dashboard executivo da operação de comunicação política."
        actions={
          <button onClick={loadSummary} className="btn-secondary">
            Atualizar
          </button>
        }
      />

      <div className="space-y-6 p-6">
        {error ? (
          <div className="rounded-3xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {cards.map((card) => (
            <div key={card.label} className="rounded-3xl border border-border bg-white p-5 shadow-sm">
              <p className="text-sm font-semibold text-muted-foreground">{card.label}</p>
              <p className="mt-3 text-3xl font-semibold text-foreground">
                {loading ? '...' : formatNumber(card.value)}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{loading ? 'Carregando dados' : card.hint}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
          <div className="rounded-3xl border border-border bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Resumo de mensagens</h2>
                <p className="mt-1 text-sm text-muted-foreground">Volume total, conversas abertas e pendências.</p>
              </div>
              <span className="rounded-full bg-accent/10 px-3 py-1 text-xs font-semibold text-accent">
                Tempo real
              </span>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl bg-muted p-4">
                <p className="text-sm text-muted-foreground">Mensagens totais</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{formatNumber(summary?.messages.total)}</p>
              </div>
              <div className="rounded-2xl bg-muted p-4">
                <p className="text-sm text-muted-foreground">Conversas ativas</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{formatNumber(summary?.conversations.active)}</p>
              </div>
              <div className="rounded-2xl bg-muted p-4">
                <p className="text-sm text-muted-foreground">Não lidas</p>
                <p className="mt-2 text-2xl font-semibold text-foreground">{formatNumber(summary?.conversations.unread)}</p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-border bg-gradient-cta p-6 text-primary-foreground shadow-elegant">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-foreground/60">
              Operação
            </p>
            <h2 className="mt-3 text-2xl font-semibold">Campanhas políticas com acompanhamento contínuo.</h2>
            <p className="mt-3 text-sm text-primary-foreground/75">
              Use a visão geral para acompanhar alcance, respostas e estabilidade dos canais antes de abrir análises detalhadas.
            </p>
          </div>
        </section>

        <section className="rounded-3xl border border-border bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Monitoramento social</p>
              <h2 className="mt-2 text-2xl font-semibold text-foreground">Sentimento de voto por tags e hashtags</h2>
              <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
                Acompanhe como eleitores estão reagindo ao candidato nas redes sociais, quais narrativas ganham tração e onde há sinais de apoio, neutralidade ou rejeição.
              </p>
            </div>
            <div className="rounded-2xl bg-accent/10 px-4 py-3 text-sm font-semibold text-accent">
              {formatNumber(socialSentiment.mentions)} menções analisadas
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-4">
            <div className="rounded-2xl bg-success/10 p-4">
              <p className="text-sm font-medium text-success">Positivo</p>
              <p className="mt-2 text-3xl font-semibold text-foreground">{socialSentiment.positive}%</p>
              <p className="mt-1 text-xs text-muted-foreground">Apoio, aprovação e intenção favorável</p>
            </div>
            <div className="rounded-2xl bg-muted p-4">
              <p className="text-sm font-medium text-muted-foreground">Neutro</p>
              <p className="mt-2 text-3xl font-semibold text-foreground">{socialSentiment.neutral}%</p>
              <p className="mt-1 text-xs text-muted-foreground">Dúvidas, comparação e menções informativas</p>
            </div>
            <div className="rounded-2xl bg-red-50 p-4">
              <p className="text-sm font-medium text-red-600">Negativo</p>
              <p className="mt-2 text-3xl font-semibold text-foreground">{socialSentiment.negative}%</p>
              <p className="mt-1 text-xs text-muted-foreground">Críticas, rejeição e temas sensíveis</p>
            </div>
            <div className="rounded-2xl bg-highlight/20 p-4">
              <p className="text-sm font-medium text-highlight-foreground">Intenção favorável</p>
              <p className="mt-2 text-3xl font-semibold text-foreground">{socialSentiment.votingIntent}%</p>
              <p className="mt-1 text-xs text-muted-foreground">Tendência {socialSentiment.trend} no período</p>
            </div>
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
            <div className="rounded-3xl border border-border bg-muted/40 p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Tags em acompanhamento</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Volume, canal dominante e sentimento favorável por termo.</p>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                {monitoredTags.map((item) => (
                  <div key={item.tag} className="rounded-2xl border border-border bg-white p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-semibold text-foreground">{item.tag}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {formatNumber(item.mentions)} menções | Canal dominante: {item.channel}
                        </p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.trend.startsWith('-') ? 'bg-red-50 text-red-600' : 'bg-success/10 text-success'}`}>
                        {item.trend}
                      </span>
                    </div>
                    <div className="mt-4">
                      <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                        <span>Sentimento favorável</span>
                        <span>{item.sentiment}%</span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-border">
                        <div className="h-full rounded-full bg-gradient-primary" style={{ width: `${item.sentiment}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-3xl border border-border bg-white p-5 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground">Alertas de narrativa</h3>
                <div className="mt-4 space-y-3">
                  {narrativeAlerts.map((alert) => (
                    <div key={alert.title} className="rounded-2xl border border-border bg-muted/40 p-4">
                      <div className="flex items-start gap-3">
                        <span className={`mt-1 h-2.5 w-2.5 rounded-full ${
                          alert.tone === 'positive' ? 'bg-success' : alert.tone === 'negative' ? 'bg-red-500' : 'bg-highlight'
                        }`} />
                        <div>
                          <p className="text-sm font-semibold text-foreground">{alert.title}</p>
                          <p className="mt-1 text-xs leading-5 text-muted-foreground">{alert.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-border bg-gradient-cta p-5 text-primary-foreground shadow-elegant">
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-foreground/60">Uso estratégico</p>
                <p className="mt-3 text-lg font-semibold">Transforme sentimento social em ação de campanha.</p>
                <p className="mt-2 text-sm text-primary-foreground/75">
                  Use as tags com alta intenção favorável para reforçar mensagens e trate temas negativos com respostas segmentadas por território.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
